import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

import { getFamilyHistoryIntake } from '../services/familyHistoryIntakeService';
import { getMemberSurveillanceOverview } from '../services/memberSurveillanceService';
import { getMemberCareTeam } from '../services/memberCareTeamService';
import { getMemberClinicalHistoryBooking } from '../services/clinicalHistoryService';
import { listMemberVisibleLabResults, listObservationsForUser } from '../services/observationService';
import { listQuarterlyReportSummaries } from '../services/quarterlyReportService';
import { listUpcomingScheduledTests } from '../services/scheduledTestService';
import {
  getPatientRecordProjection,
  type PatientRecordProjection,
} from '../services/patientRecordProjectionService';
import {
  buildTodayTrackerStrip,
  getDailyEntry,
  getTrackerSettings,
  getTrackerSummary,
  listRecentDailyEntries,
  todayKey,
  type TrackerStripItem,
} from '../services/dailyBiteService';
import {
  subscribeMemberConversations,
  summariseUnread,
  type MemberConversation,
} from '../services/memberConversationService';

import { DEFAULT_TRACKER_SETTINGS } from '../types/dailyBite';
import { computeIntakeReadiness } from '../types/intakeReadiness';
import type { IntakeProgress } from '../components/dashboard/home/IntakePrompt';
import { getMemberFirstName } from '../utils/memberDisplayName';
import { coerceDate } from '../utils/dateLabels';
import { buildDueActionCards, type DueActionCard } from '../utils/memberEngagement';

import { INITIAL_SOURCES, type BookingSummary, type DashboardSources, type SourceStatus } from '../utils/dashboard/types';
import { resolveLastReviewAnchor, type ReviewAnchor } from '../utils/dashboard/reviewAnchor';
import { computeNextStep, type NextStepAction, type NextStepRank } from '../utils/dashboard/nextStep';
import { isBatchUnseen, listResultDraws, summariseLatestResultsBatch } from '../utils/dashboard/newResults';
import { buildHealthSnapshot, type SnapshotChip } from '../utils/dashboard/healthSnapshot';
import { buildBiomarkerDeltas, type BiomarkerDelta } from '../utils/dashboard/biomarkerDeltas';
import { buildWearableDeltas, type WearableDelta } from '../utils/dashboard/wearableDeltas';
import { buildQuarterStepper, currentQuarterLabel, type QuarterProgress } from '../utils/dashboard/quarterStepper';
import { buildComingUp, type ComingUpEvent } from '../utils/dashboard/comingUp';
import { buildDashboardActivity, type ActivityEntry } from '../utils/dashboard/dashboardActivity';

import type { MemberCareTeamMember } from '../types/memberCareTeam';
import type { LabResultRecord } from '../types/labs';
import type { ObservationRecord } from '../types/observations';
import type { QuarterlyReportSummary } from '../types/quarterlyReport';
import type { ScheduledTestRecord } from '../services/scheduledTestService';
import type { SurveillancePlanRecord, SurveillanceReviewPrompt } from '../types/surveillance';
import type { PublishedFamilyTreeRecord } from '../types/familyTree';

/**
 * Everything the member dashboard renders, in two tiers.
 *
 * PER-READ GUARDING (carried over verbatim in intent from MemberHomePage):
 * each read is individually guarded. The home page aggregates a dozen
 * independent reads, and before this a single rejection (e.g. a Firestore rule
 * mismatch on one subcollection) sank the whole Promise.all and blanked every
 * section — including the care team, which read fine on its own. Degrade
 * per-read instead.
 *
 * What has changed is the SHAPE of the guard. `.catch(() => [])` cannot tell
 * "you have no lab results" from "the lab read was denied", so a rules
 * regression rendered as a confident zero. `guard()` records which of the two
 * happened, and the builders refuse to render a count from a failed source.
 *
 * TIERS: Tier A is what a member sees first and is smaller than the old single
 * Promise.all, so first paint is faster. Tier B fills in the analytical blocks
 * and re-runs the next-step ranker, so the hero upgrades in place.
 */

export interface MemberDashboardToday {
  strip: TrackerStripItem[];
  loggedCount: number;
  total: number;
  streak: number;
  optedInCount: number;
  unlogged: number;
}

export interface MemberDashboardState {
  phase: 'loading' | 'ready';
  tierBLoading: boolean;
  sources: DashboardSources;
  firstName: string;
  nextStep: NextStepAction | null;
  snapshot: SnapshotChip[];
  reviewAnchor: ReviewAnchor;
  biomarkerDeltas: BiomarkerDelta[];
  wearableDeltas: WearableDelta[];
  /**
   * Set only when the member has exactly one draw on file. Deltas need two
   * readings of a marker, so a first panel produces none and "What's changed"
   * used to collapse — leaving the member who had just got their first results
   * with the emptiest Home in the product. This is what that block says instead.
   */
  resultsBaseline: { count: number; observedAt: Date } | null;
  quarter: QuarterProgress;
  watchlist: DueActionCard[];
  planReleasedAt: Date | null;
  today: MemberDashboardToday;
  careTeam: MemberCareTeamMember[];
  comingUp: ComingUpEvent[];
  activity: ActivityEntry[];
  labResults: LabResultRecord[];
  wearableObservations: ObservationRecord[];
  openPrompts: SurveillanceReviewPrompt[];
  latestPlan: SurveillancePlanRecord | null;
  familyTree: PublishedFamilyTreeRecord | null;
  /**
   * Intake completeness, surfaced in its own right.
   *
   * `nextStep` already knows about an unfinished history, but it ranks it
   * seventh of eight — below results, reports and messages. Any member with a
   * panel back therefore never saw it, which is most of them, and the only
   * other route to /history is a link in the last block on the page. The rail
   * has no slot for it at all. Exposing the readiness separately lets Home
   * prompt for the intake regardless of what won the ranking.
   *
   * `null` when there is no intake to judge OR when the read failed — a denied
   * read must not tell a member their history is incomplete.
   */
  // The shape IntakePrompt consumes, imported rather than restated: this was a
  // structural duplicate of it, so adding a field to one silently failed to
  // reach the other.
  intakeProgress: IntakeProgress | null;
}

interface TierAData {
  userData: Record<string, any>;
  intake: any;
  overview: { latestPlan: SurveillancePlanRecord | null; openPrompts: SurveillanceReviewPrompt[]; familyTree: PublishedFamilyTreeRecord | null };
  trackerSettings: typeof DEFAULT_TRACKER_SETTINGS;
  todayEntry: any;
  trackerSummary: any;
  booking: BookingSummary;
  careTeam: MemberCareTeamMember[];
  recentEntries: any[];
}

interface TierBData {
  labResults: LabResultRecord[];
  labObservations: ObservationRecord[];
  wearableObservations: ObservationRecord[];
  reports: QuarterlyReportSummary[];
  scheduledTests: ScheduledTestRecord[];
  projection: PatientRecordProjection | null;
}

/** Reports the member has opened. There is no server-side read receipt. */
const OPENED_REPORTS_KEY = (uid: string) => `melio.openedReports.${uid}`;

function readOpenedReports(uid: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(OPENED_REPORTS_KEY(uid)) || '[]'));
  } catch {
    return new Set();
  }
}

export function markReportOpened(uid: string, reportId: string): void {
  try {
    const opened = readOpenedReports(uid);
    opened.add(reportId);
    localStorage.setItem(OPENED_REPORTS_KEY(uid), JSON.stringify([...opened]));
  } catch {
    // A full or disabled localStorage only costs a repeated nudge.
  }
}

/**
 * Watermark for results the member has looked at. A timestamp rather than a set
 * of ids, because the event being acknowledged is a draw — thirty markers the
 * member saw in one sitting — not thirty separate things to tick off.
 *
 * Same storage and same caveat as the opened-reports receipt above: there is no
 * server-side read receipt, so this is per-device. `isBatchUnseen` carries a
 * horizon precisely so a missing watermark on a new device cannot resurrect an
 * old panel as news.
 */
const SEEN_RESULTS_KEY = (uid: string) => `melio.seenResultsAt.${uid}`;

function readSeenResultsAt(uid: string): Date | null {
  try {
    const raw = localStorage.getItem(SEEN_RESULTS_KEY(uid));
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

export function markResultsSeen(uid: string, observedAt: Date): void {
  try {
    const existing = readSeenResultsAt(uid);
    // Never move the watermark backwards: a member opening an old panel has not
    // thereby acknowledged a newer one.
    if (existing && existing.getTime() >= observedAt.getTime()) return;
    localStorage.setItem(SEEN_RESULTS_KEY(uid), observedAt.toISOString());
  } catch {
    // A full or disabled localStorage only costs a repeated nudge.
  }
}

const EMPTY_TODAY: MemberDashboardToday = {
  strip: [],
  loggedCount: 0,
  total: 0,
  streak: 0,
  optedInCount: 0,
  unlogged: 0,
};

const EMPTY_QUARTER: QuarterProgress = {
  quarterLabel: currentQuarterLabel(new Date()),
  steps: [],
  expectedReportLabel: null,
};

const INITIAL_STATE: MemberDashboardState = {
  phase: 'loading',
  tierBLoading: true,
  sources: INITIAL_SOURCES,
  firstName: 'there',
  nextStep: null,
  snapshot: [],
  reviewAnchor: { at: null, label: 'No previous review yet', source: 'none' },
  biomarkerDeltas: [],
  wearableDeltas: [],
  resultsBaseline: null,
  intakeProgress: null,
  quarter: EMPTY_QUARTER,
  watchlist: [],
  planReleasedAt: null,
  today: EMPTY_TODAY,
  careTeam: [],
  comingUp: [],
  activity: [],
  labResults: [],
  wearableObservations: [],
  openPrompts: [],
  latestPlan: null,
  familyTree: null,
};

function isEmptyish(value: unknown): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Map || value instanceof Set) return value.size === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

export default function useMemberDashboard(uid?: string | null): MemberDashboardState {
  const [state, setState] = useState<MemberDashboardState>(INITIAL_STATE);
  const [conversations, setConversations] = useState<MemberConversation[]>([]);
  const [conversationsStatus, setConversationsStatus] = useState<SourceStatus>('empty');
  const tierA = useRef<TierAData | null>(null);
  const tierB = useRef<TierBData | null>(null);

  // ── Live: unread messages ─────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    return subscribeMemberConversations(
      uid,
      (next) => {
        setConversations(next);
        setConversationsStatus(next.length > 0 ? 'ok' : 'empty');
      },
      (error) => {
        console.warn('[dashboard] conversations read failed', error);
        setConversationsStatus('failed');
      },
    );
  }, [uid]);

  // ── Tier A + Tier B ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return undefined;
    let active = true;
    const sources: DashboardSources = { ...INITIAL_SOURCES };

    const guard = <T,>(promise: Promise<T>, key: keyof DashboardSources, fallback: T): Promise<T> =>
      promise
        .then((value) => {
          sources[key] = isEmptyish(value) ? 'empty' : 'ok';
          return value;
        })
        .catch((error) => {
          console.warn(`[dashboard] ${key} read failed`, error);
          sources[key] = 'failed';
          return fallback;
        });

    const run = async () => {
      const now = new Date();

      // ── Tier A: what the member sees first ────────────────────────────────
      const [userSnap, intake, overview, trackerSettings, todayEntry, trackerSummary, booking, recentEntries] =
        await Promise.all([
          guard(getDoc(doc(db, 'users', uid)), 'careTeam', null as any),
          guard(getFamilyHistoryIntake(uid), 'intake', null),
          guard(getMemberSurveillanceOverview(uid), 'plan', {
            latestPlan: null,
            openPrompts: [],
            familyTree: null,
          }),
          guard(getTrackerSettings(uid), 'dailyBite', { ...DEFAULT_TRACKER_SETTINGS }),
          getDailyEntry(uid, todayKey()).catch(() => null),
          getTrackerSummary(uid).catch(() => null),
          guard(getMemberClinicalHistoryBooking(uid), 'booking', {
            status: 'none' as const,
            bookingId: null,
            slotId: null,
            startAt: null,
            endAt: null,
            doctorName: null,
            meetingLink: null,
          }),
          listRecentDailyEntries(uid, 120).catch(() => []),
        ]);

      if (!active) return;

      const userData = userSnap?.exists?.() ? (userSnap.data() as Record<string, any>) : {};
      const careTeamView = await getMemberCareTeam(uid, {
        userData,
        intake,
        email: userData.email,
        displayName: userData.displayName,
      }).catch(() => ({ members: [] as MemberCareTeamMember[], isEmpty: true }));
      sources.careTeam = careTeamView.members.length > 0 ? 'ok' : 'empty';

      tierA.current = {
        userData,
        intake,
        overview,
        trackerSettings,
        todayEntry,
        trackerSummary,
        booking,
        careTeam: careTeamView.members,
        recentEntries,
      };

      if (!active) return;
      setState((previous) => ({
        ...previous,
        ...composeTierA(tierA.current!, sources, now),
        phase: 'ready',
        tierBLoading: true,
        sources: { ...sources },
      }));

      // ── Tier B: the analytical blocks ─────────────────────────────────────
      const [labResults, labObservations, wearableObservations, reports, scheduledTests, projection] =
        await Promise.all([
          guard(listMemberVisibleLabResults(uid), 'labs', [] as LabResultRecord[]),
          listObservationsForUser(uid, 'lab').catch(() => [] as ObservationRecord[]),
          guard(listObservationsForUser(uid, 'wearable'), 'wearable', [] as ObservationRecord[]),
          guard(listQuarterlyReportSummaries(uid), 'reports', [] as QuarterlyReportSummary[]),
          guard(listUpcomingScheduledTests(uid), 'scheduledTests', [] as ScheduledTestRecord[]),
          // Not guarded into `sources`: this is an enhancement to the quarter
          // stepper, not a source any block depends on. A member with no
          // projection simply falls back to the derivation.
          getPatientRecordProjection(uid).catch(() => null),
        ]);

      if (!active) return;
      tierB.current = { labResults, labObservations, wearableObservations, reports, scheduledTests, projection };

      setState((previous) => ({
        ...previous,
        ...composeTierA(tierA.current!, sources, now),
        ...composeTierB(uid, tierA.current!, tierB.current!, sources, now),
        phase: 'ready',
        tierBLoading: false,
        sources: { ...sources },
      }));
    };

    run().catch((error) => {
      console.error('[dashboard] load failed', error);
      if (active) setState((previous) => ({ ...previous, phase: 'ready', tierBLoading: false }));
    });

    return () => {
      active = false;
    };
  }, [uid]);

  // The next step depends on live unread counts, so it is recomputed here
  // rather than baked into the tiered snapshots above.
  const unread = useMemo(() => summariseUnread(conversations), [conversations]);

  return useMemo(() => {
    if (!uid || state.phase === 'loading') return state;

    const unavailableRanks: NextStepRank[] = [];
    if (conversationsStatus === 'failed') {
      unavailableRanks.push('urgent_message', 'unread_message');
    }
    if (state.sources.plan === 'failed') unavailableRanks.push('overdue_action');
    if (state.sources.scheduledTests === 'failed') unavailableRanks.push('testing_due');
    if (state.sources.labs === 'failed') unavailableRanks.push('results_ready');
    if (state.sources.reports === 'failed') unavailableRanks.push('report_available');
    if (state.sources.intake === 'failed') unavailableRanks.push('incomplete_history');
    if (state.sources.dailyBite === 'failed') unavailableRanks.push('daily_bite');

    const overdueItems = (tierA.current?.overview.latestPlan?.items || [])
      .map((item) => {
        const dueAt = coerceDate(item.nextDue);
        if (!dueAt) return null;
        const daysOverdue = Math.floor((Date.now() - dueAt.getTime()) / (24 * 60 * 60 * 1000));
        if (daysOverdue <= 0) return null;
        return { title: `${item.modality}: ${item.action}`, dueAt, daysOverdue };
      })
      .filter((item): item is { title: string; dueAt: Date; daysOverdue: number } => item !== null);

    const openedReports = readOpenedReports(uid);
    const unopenedReport =
      (tierB.current?.reports || []).find((report) => !openedReports.has(report.id)) || null;

    const sexForRanges =
      tierA.current?.userData.sexAtBirth ||
      tierA.current?.userData.sex ||
      tierA.current?.userData.gender ||
      tierA.current?.intake?.personalInfo?.sexAtBirth ||
      null;
    const latestBatch = summariseLatestResultsBatch(tierB.current?.labResults || [], sexForRanges);
    const unseenResults =
      latestBatch && isBatchUnseen(latestBatch, readSeenResultsAt(uid))
        ? {
            count: latestBatch.count,
            outOfRangeCount: latestBatch.outOfRangeCount,
            isFirstPanel: latestBatch.isFirstPanel,
          }
        : null;

    const nextScheduledTest = tierB.current?.scheduledTests?.[0]
      ? {
          name: tierB.current.scheduledTests[0].name,
          scheduledFor: coerceDate(tierB.current.scheduledTests[0].scheduledFor),
        }
      : null;

    const readiness = tierA.current?.intake ? computeIntakeReadiness(tierA.current.intake) : null;

    const nextStep = computeNextStep({
      hasUrgentMessage: unread.hasUrgent,
      unreadCount: unread.total,
      unreadFrom: unread.latest?.clinicianName || null,
      overdueItems,
      nextScheduledTest,
      unopenedReport: unopenedReport
        ? { id: unopenedReport.id, quarterLabel: unopenedReport.quarterLabel }
        : null,
      unseenResults,
      readiness: readiness ? { completionRatio: readiness.completionRatio, ready: readiness.ready } : null,
      bookingStatus: tierA.current?.booking.status || 'none',
      dailyBite: {
        optedInCount: state.today.optedInCount,
        unlogged: state.today.unlogged,
        streak: state.today.streak,
      },
      unavailableRanks,
    });

    return { ...state, nextStep };
  }, [uid, state, unread, conversationsStatus]);
}

// ── Composition ─────────────────────────────────────────────────────────────

function composeTierA(
  data: TierAData,
  sources: DashboardSources,
  now: Date,
): Partial<MemberDashboardState> {
  const strip = buildTodayTrackerStrip(data.todayEntry, data.trackerSettings);
  const loggedCount = strip.filter((item) => item.logged).length;
  const readiness = data.intake ? computeIntakeReadiness(data.intake) : null;

  return {
    firstName: getMemberFirstName({
      firstName: data.userData.firstName,
      displayName: data.userData.displayName,
    }),
    careTeam: data.careTeam,
    intakeProgress:
      readiness && sources.intake !== 'failed'
        ? {
            ready: readiness.ready,
            completionRatio: readiness.completionRatio,
            requiredPassed: readiness.requiredPassed,
            requiredTotal: readiness.requiredTotal,
            bookingStatus: data.booking.status,
            // So the prompt can say what is actually left rather than only how
            // far through they are. The percentage counts five clinical
            // prerequisites; naming them is the part a member can act on.
            outstandingIds: readiness.blockers.map((blocker) => blocker.id),
          }
        : null,
    latestPlan: data.overview.latestPlan,
    openPrompts: data.overview.openPrompts,
    familyTree: data.overview.familyTree,
    planReleasedAt: coerceDate(data.overview.latestPlan?.releasedAt),
    watchlist: buildDueActionCards(data.overview.latestPlan),
    today: {
      strip,
      loggedCount,
      total: strip.length,
      streak: data.trackerSummary?.currentStreak ?? 0,
      optedInCount: strip.length,
      unlogged: strip.length - loggedCount,
    },
    quarter: buildQuarterStepper({
      now,
      readiness: readiness ? { completionRatio: readiness.completionRatio, ready: readiness.ready } : null,
      intakeStatus: sources.intake,
      bookingStatus: data.booking.status,
      bookingSourceStatus: sources.booking,
      latestLabObservationAt: null,
      labsStatus: sources.labs,
      hasOpenBloodPanel: false,
      scheduledTestsStatus: sources.scheduledTests,
      latestWearableObservationAt: null,
      wearableStatus: sources.wearable,
      dailyEntriesThisQuarter: countEntriesThisQuarter(data.recentEntries, now),
      planReleasedAt: coerceDate(data.overview.latestPlan?.releasedAt),
      planStatus: sources.plan,
      releasedReportQuarterLabels: [],
      reportsStatus: sources.reports,
      reviewAnchorAt: null,
    }),
  };
}

function composeTierB(
  uid: string,
  a: TierAData,
  b: TierBData,
  sources: DashboardSources,
  now: Date,
): Partial<MemberDashboardState> {
  const sex =
    a.userData.sexAtBirth || a.userData.sex || a.userData.gender || a.intake?.personalInfo?.sexAtBirth || null;

  const observationDates = [...b.labObservations, ...b.wearableObservations]
    .map((observation) => coerceDate(observation.observedAt))
    .filter((date): date is Date => date !== null)
    .sort((left, right) => left.getTime() - right.getTime());

  const reviewAnchor = resolveLastReviewAnchor({
    releasedReports: b.reports,
    latestPlan: a.overview.latestPlan,
    earliestObservationAt: observationDates[0] || null,
  });

  const latestLabAt = latestDate(b.labObservations);
  const latestWearableAt = latestDate(b.wearableObservations);
  const readiness = a.intake ? computeIntakeReadiness(a.intake) : null;

  return {
    labResults: b.labResults,
    wearableObservations: b.wearableObservations,
    reviewAnchor,
    snapshot: buildHealthSnapshot({
      labs: b.labResults,
      labsStatus: sources.labs,
      sex,
      openPrompts: a.overview.openPrompts,
      promptsStatus: sources.plan,
      nextScheduledTest: b.scheduledTests[0]
        ? { name: b.scheduledTests[0].name, scheduledFor: coerceDate(b.scheduledTests[0].scheduledFor) }
        : null,
      scheduledTestsStatus: sources.scheduledTests,
      readiness: readiness ? { completionRatio: readiness.completionRatio } : null,
      intakeStatus: sources.intake,
      activePlanItemCount: a.overview.latestPlan?.items?.length ?? 0,
      planStatus: sources.plan,
      now,
    }),
    biomarkerDeltas: buildBiomarkerDeltas(b.labResults, sex, { since: reviewAnchor.at }),
    wearableDeltas: buildWearableDeltas(b.wearableObservations, now),
    resultsBaseline: (() => {
      const draws = listResultDraws(b.labResults, sex);
      // Exactly one: with two or more there is real movement to show, and with
      // none there is nothing to be a baseline of.
      if (draws.length !== 1) return null;
      return { count: draws[0].count, observedAt: draws[0].collectedAt || draws[0].observedAt };
    })(),
    quarter: buildQuarterStepper({
      now,
      readiness: readiness ? { completionRatio: readiness.completionRatio, ready: readiness.ready } : null,
      intakeStatus: sources.intake,
      bookingStatus: a.booking.status,
      bookingSourceStatus: sources.booking,
      latestLabObservationAt: latestLabAt,
      labsStatus: sources.labs,
      hasOpenBloodPanel: b.scheduledTests.some((scheduled) => scheduled.category === 'blood_panel'),
      scheduledTestsStatus: sources.scheduledTests,
      latestWearableObservationAt: latestWearableAt,
      wearableStatus: sources.wearable,
      dailyEntriesThisQuarter: countEntriesThisQuarter(a.recentEntries, now),
      planReleasedAt: coerceDate(a.overview.latestPlan?.releasedAt),
      planStatus: sources.plan,
      releasedReportQuarterLabels: b.reports.map((report) => report.quarterLabel),
      reportsStatus: sources.reports,
      reviewAnchorAt: reviewAnchor.at,
      reportCycle: b.projection?.reportCycle
        ? {
            reportStatus: b.projection.reportCycle.reportStatus,
            deliveryCallAt: coerceDate(b.projection.reportCycle.deliveryCallAt),
          }
        : null,
    }),
    comingUp: buildComingUp({
      now,
      scheduledTests: b.scheduledTests,
      scheduledTestsStatus: sources.scheduledTests,
      booking: a.booking,
      bookingStatus: sources.booking,
      plan: a.overview.latestPlan,
      planStatus: sources.plan,
    }),
    activity: buildDashboardActivity({
      labObservations: b.labObservations,
      labsStatus: sources.labs,
      wearableObservations: b.wearableObservations,
      wearableStatus: sources.wearable,
      releasedReports: b.reports,
      reportsStatus: sources.reports,
      plan: a.overview.latestPlan,
      planStatus: sources.plan,
      familyTree: a.overview.familyTree,
    }),
  };
}

function latestDate(observations: ObservationRecord[]): Date | null {
  return observations
    .map((observation) => coerceDate(observation.observedAt))
    .filter((date): date is Date => date !== null)
    .sort((left, right) => right.getTime() - left.getTime())[0] || null;
}

function countEntriesThisQuarter(entries: { date?: string }[], now: Date): number {
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  return entries.filter((entry) => {
    const date = coerceDate(entry.date);
    return date != null && date >= quarterStart;
  }).length;
}
